/**
 * Finds the amount value by dichotomy to obtain a target value
 * @param target - The target value to reach
 * @param runWaterfall - The function that calculates the value for a given amount
 * @param tolerance - The accepted tolerance (default: 0.01)
 * @param maxIterations - Maximum number of iterations (default: 100)
 * @returns The amount value that gives a value close to target
 */
export function getBreakeven(
  target: number,
  runWaterfall: (amount: number) => number,
  tolerance = 0.01,
  maxIterations = 50
): number {
  // Step 1: Find an interval [min, max] that contains the solution
  let min = 0;
  let max = 100_000_000; // High initial value

  // Check that min gives a value lower than target
  let minValue = runWaterfall(min);
  if (minValue > target) {
    // If min is too large, divide until we find a lower bound
    while (minValue > target && maxIterations > 0) {
      max = min;
      min = min / 2;
      minValue = runWaterfall(min);
      maxIterations--;
    }
  }

  // Check that max gives a value higher than target
  let maxValue = runWaterfall(max);
  if (maxValue < target) {
    // If max is too small, double until we find an upper bound
    while (maxValue < target && maxIterations > 0) {
      max *= 2;
      maxValue = runWaterfall(max);
      maxIterations--;
    }
  }

  // Step 2: Classic dichotomy - find the smallest solution
  let iterations = 0;
  let foundSolution: number | null = null;

  while (iterations < maxIterations) {
    const middle = (min + max) / 2;
    const middleValue = runWaterfall(middle);

    // Check if we have reached the desired precision
    if (Math.abs(middleValue - target) <= tolerance) {
      foundSolution = middle;
      // We are looking for the smallest solution, so we reduce the interval to the left
      max = middle;
    } else {
      // Adjust the interval according to the result
      if (middleValue > target) {
        max = middle; // The solution is smaller
      } else {
        min = middle; // The solution is larger
      }
    }

    iterations++;
  }
  return foundSolution;
}